import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import type {
  ActivitySubjectActionSummary,
  ActivitySubjectCard,
} from '@shared/types/activity-feed';
import { PREBUILT_TOOL_MANIFESTS } from '@shared/types/tool-manifest';
import type { LightViewAction } from '@shared/types/views';
import { WorkActionCard } from '@/components/work/WorkActionCard';
import { DENSE_ACTION_CLASS, WorkActionControl } from '@/components/work/WorkActionControl';
import { ViewActionButton } from '@/components/views/atoms/ViewActionButton';
import { getSubjectWorkActions } from '@/lib/work-actions';
import { buildSubjectThreadPath } from '@/lib/thread-routes';
import { useOptionalAppAssistant } from '@/lib/app-assistant-context';
import {
  isRedundantVisibleLabel,
  sweepBusinessVocabulary,
  sweepSerializedBusinessText,
} from '@/lib/ui-editorial-sweeper';
import type { ActivityActionLaunchStatus, ActivityActionTriageReason } from './ActivityActionQueue';
import { getActivityActionTriageId } from './ActivityActionQueue';
import {
  findTopicActionSummary,
  THREAD_VISIBLE_STATE_LABELS,
  type ActivityLivingWorkProjection,
} from './thread-living-model';
import { getWorkVerb } from './today-pipeline';
import { AccountTaskVisual, TopicStatusIcon } from './work-row-visuals';
import { TopicPlanItems } from './TopicPlanItems';
import { GuidanceMotifItems } from './GuidanceMotifItems';
import type { WorkGuidanceMotif } from '@/lib/work-guidance';
import type { TodayBucket, TodayCommitment } from './today-horizons';

/** J5: how the row is held in Aujourd'hui; absent outside Aujourd'hui. */
export interface RowPlan {
  commitment: TodayCommitment;
  bucket: TodayBucket;
  plannable: boolean;
  /** Short data shown by the state (age, planned day). */
  note: string | null;
  /** Relais: the reply of the colleague who sent the task back. */
  reply?: string | null;
  /** The step the task now proposes, when its first title is already done. */
  title?: string | null;
  /** Its last finished action (« ✓ 13 oct. · Ajouter au CRM »). */
  done?: { text: string; failed: boolean } | null;
  /** Under its company's first task: the company is not repeated. */
  nested?: boolean;
}

type ToolHandoffAction = Extract<LightViewAction, { type: 'tool_handoff' }>;
type FlowHandoffAction = Extract<LightViewAction, { type: 'flow_handoff' }>;

export interface LivingWorkHandlers {
  onTriageAction: (
    subject: ActivitySubjectCard,
    action: ActivitySubjectActionSummary,
    reason: ActivityActionTriageReason,
    guidance?: { motif: WorkGuidanceMotif; note: string | null }
  ) => void;
  onToolHandoff: (
    subject: ActivitySubjectCard,
    action: ActivitySubjectActionSummary,
    toolAction: ToolHandoffAction
  ) => void;
  onFlowHandoff: (
    subject: ActivitySubjectCard,
    action: ActivitySubjectActionSummary,
    flowAction: FlowHandoffAction
  ) => void;
  triagingActionId: string | null;
  launchStatuses: Record<string, ActivityActionLaunchStatus>;
}

const TOOL_VISIBLE_LABELS = Object.values(PREBUILT_TOOL_MANIFESTS).flatMap(
  ({ product, launch }) => [
    product.title,
    launch.launchLabel,
    launch.composerTitle,
    launch.submitLabel,
  ]
);

/** One line under a task: what was last done on it. */
export const DoneLine = ({ text, failed }: { text: string; failed: boolean }) => (
  <p title={text} className={`mt-0.5 truncate text-xs leading-4 ${failed ? 'text-domino-inkSoft' : 'text-emerald-700'}`}>{text}</p>
);

export const getAccountSecondaryLabel = (
  subject: ActivitySubjectCard,
  work: ReturnType<typeof getSubjectWorkActions>[number]
): string | null => {
  const subtitle = subject.subjectSubtitle?.trim();
  if (!subtitle) return null;
  const commandLabel = 'label' in work.command ? work.command.label : null;
  const ownedLabels = [
    subject.subjectLabel,
    subject.lastToolLabel,
    subject.nextStepLabel,
    work.title,
    commandLabel,
  ].filter((value): value is string => Boolean(value));
  const distinctParts = subtitle
    .split('·')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => sweepBusinessVocabulary(part))
    .filter(
      (part) =>
        !isRedundantVisibleLabel(part, subject.subjectLabel) &&
        !ownedLabels.some((label) => isRedundantVisibleLabel(label, part)) &&
        !TOOL_VISIBLE_LABELS.some((label) => isRedundantVisibleLabel(label, part))
    );
  return distinctParts.length > 0 ? distinctParts.join(' · ') : null;
};

/** One account work in the list of Aujourd'hui. */
export const LivingWorkRow = ({
  item,
  triagingActionId,
  launchStatuses,
  onTriageAction,
  onToolHandoff,
  onFlowHandoff,
  plan,
  identity = 'account',
}: LivingWorkHandlers & {
  item: ActivityLivingWorkProjection;
  plan?: RowPlan;
  /** On the page of an account the account is the page's title: the first cell says the state. */
  identity?: 'account' | 'state';
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const assistant = useOptionalAppAssistant();
  const { subject, actionSubject, topic, work } = item;
  const action = findTopicActionSummary(actionSubject, topic);
  const actionId = action ? getActivityActionTriageId(actionSubject, action) : null;
  const launchStatus = actionId ? (launchStatuses[actionId] ?? null) : null;
  const threadPath = buildSubjectThreadPath(subject);
  const visibleWorkTitle = sweepBusinessVocabulary(
    sweepSerializedBusinessText(work.title)
  );
  const isTriaging = actionId !== null && triagingActionId === actionId;
  const secondaryLabel = getAccountSecondaryLabel(subject, work);
  const canTriage = Boolean(action && action.dismissible !== false);
  const canPlan = Boolean(plan?.plannable);
  const hasMenu = Boolean(assistant) || canTriage || canPlan;
  const headingMenu = hasMenu ? (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-label={`Options pour ${visibleWorkTitle}`}
        aria-expanded={menuOpen}
        disabled={isTriaging}
        onClick={() => setMenuOpen((current) => !current)}
        className="inline-flex h-8 w-8 items-center justify-center rounded text-domino-inkSoft transition hover:bg-domino-muted hover:text-domino-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune disabled:opacity-50"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {menuOpen && (
        <div className="absolute right-0 top-9 z-20 w-56 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white py-1 shadow-[var(--domino-shadow-elevated)]">
          {plan && canPlan && (
            <TopicPlanItems topic={topic} commitment={plan.commitment} bucket={plan.bucket} onDone={() => setMenuOpen(false)} />
          )}
          {assistant && (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                assistant.openWork(work.context);
              }}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-domino-ink hover:bg-domino-muted"
            >
              Ajuster la demande
            </button>
          )}
          {canTriage && action && !canPlan && (
            <>
              {assistant && <div className="my-1 border-t border-domino-cardBorder" />}
              <GuidanceMotifItems
                disabled={isTriaging}
                onPick={(motif, note) => {
                  setMenuOpen(false);
                  onTriageAction(actionSubject, action, motif === 'done' ? 'already_done' : 'not_relevant', { motif, note });
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  ) : null;

  const accountIdentity = (
    <div className="flex min-w-0 items-center gap-2.5">
      <AccountTaskVisual
        imageUrl={subject.imageUrl}
        actionKind={action?.kind ?? null}
        subjectKind={subject.subjectKind}
        taskTitle={work.title}
      />
      <div className="min-w-0">
        <Link
          to={threadPath}
          title={subject.subjectLabel}
          className="block truncate text-sm font-semibold leading-5 text-domino-aubergine hover:underline focus-visible:ring-2 focus-visible:ring-domino-prune"
        >
          {subject.subjectLabel}
        </Link>
        {secondaryLabel && (
          <p title={secondaryLabel} className="truncate text-xs leading-4 text-domino-inkSoft">
            {secondaryLabel}
          </p>
        )}
        <span className="flex items-center gap-1 text-xs leading-4 text-domino-inkSoft">
          <TopicStatusIcon state={topic.visibleState} />
          {THREAD_VISIBLE_STATE_LABELS[topic.visibleState]}
          {plan?.note && <span className="tabular-nums">· {plan.note}</span>}
        </span>
        {plan?.reply && <p title={plan.reply} className="truncate text-xs leading-4 text-domino-aubergine">{plan.reply}</p>}
      </div>
    </div>
  );
  const stateIdentity = (
    <div className="min-w-0">
      <span className="flex items-center gap-1.5 text-sm leading-5 text-domino-ink">
        <TopicStatusIcon state={topic.visibleState} />
        {THREAD_VISIBLE_STATE_LABELS[topic.visibleState]}
      </span>
      {plan?.note && <span className="block text-xs leading-4 tabular-nums text-domino-inkSoft">{plan.note}</span>}
      {plan?.reply && <p title={plan.reply} className="truncate text-xs leading-4 text-domino-aubergine">{plan.reply}</p>}
    </div>
  );
  const controls = launchStatus ? (
    launchStatus.href ? (
      <Link to={launchStatus.href} className="text-sm text-domino-aubergine">
        {launchStatus.label}
      </Link>
    ) : (
      <span role="status" className="text-sm text-domino-inkSoft">
        {launchStatus.label}
      </span>
    )
  ) : work.command.kind === 'execute' && action ? (
    <ViewActionButton
      action={work.command.action}
      className={`h-auto min-h-8 max-w-full px-3 py-1 text-xs leading-4 shadow-none ${DENSE_ACTION_CLASS}`}
      onToolHandoff={(toolAction) => onToolHandoff(actionSubject, action, toolAction)}
      onFlowHandoff={(flowAction) => onFlowHandoff(actionSubject, action, flowAction)}
    />
  ) : (
    <WorkActionControl work={work} showAdjustment={false} dense label={getWorkVerb(work) ?? undefined} />
  );

  return (
    <article className="rounded-lg border border-domino-cardBorder bg-domino-white px-3.5 py-3 transition-colors hover:bg-domino-muted/30 sm:rounded-none sm:border-0">
      <WorkActionCard
        work={plan?.title ? { ...work, title: plan.title } : work}
        compact
        layout="row"
        headingAction={headingMenu}
        leading={identity === 'state' ? stateIdentity : plan?.nested ? null : accountIdentity}
        controls={controls}
        subline={plan?.done ? <DoneLine {...plan.done} /> : null}
      />
    </article>
  );
};
